/**
 * Persona Card (revamp T25) — the settings surface for the investor persona.
 *
 * Shows the user's current knowledge level (the newest user_personas version,
 * via usePersona) as an editable picker: a change appends a new version with
 * source='user_edit' through useCreatePersonaVersion and invalidates the
 * ['persona'] cache. The style/risk summary line is READ-ONLY here — settings
 * has no chat context, so it is read from the portfolio thesis store
 * (usePortfolioThesis, newest-first) instead; editing those lives in the
 * portfolio thesis form and the chat profile builder. The answer-style row is
 * a separate ProfileScreen section and is deliberately not duplicated.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { usePersona, useCreatePersonaVersion } from '../../services/api/queries/usePersona';
import { usePortfolioThesis } from '../../services/api/queries/usePortfolioThesis';
import type { KnowledgeLevel } from '../../services/api/personaApi';
import { KNOWLEDGE_LEVELS } from '../../services/api/personaApi';
import { showError } from '../../utils/alert';

const LEVEL_LABEL_KEYS: Record<KnowledgeLevel, string> = {
  beginner: 'chat.knowledgeBeginner',
  intermediate: 'chat.knowledgeIntermediate',
  advanced: 'chat.knowledgeAdvanced',
  expert: 'chat.knowledgeExpert',
};

export function PersonaCard() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: persona } = usePersona();
  const createPersonaVersion = useCreatePersonaVersion();
  const { data: theses } = usePortfolioThesis();
  const [saving, setSaving] = useState(false);
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // getPortfolioThesis is newest-first (the thesis form reads theses[0] the
  // same way): the live style/risk summary the chat agent also grounds on.
  const latestThesis = theses?.[0] ?? null;
  const styleName = latestThesis?.style_name || t('settings.personaNotSet');
  const riskLevel = latestThesis?.target_risk_level || t('settings.personaNotSet');

  const handleLevelChange = async (level: '' | KnowledgeLevel) => {
    // The no-selection placeholder (rendered only while persona is null) has
    // an empty value — re-picking it must not post a version.
    if (!level || saving) return;
    setSaving(true);
    try {
      // Append-only: every change is a new version (source='user_edit'). The
      // mutation seeds the ['persona'] cache with the returned read model;
      // the invalidate additionally reconciles against the server.
      await createPersonaVersion.mutateAsync({ knowledge_level: level, source: 'user_edit' });
      await queryClient.invalidateQueries({ queryKey: ['persona'] });
    } catch {
      showError(t('settings.personaUpdateFailed', { defaultValue: 'Failed to update persona' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.sectionContainer}>
      <Text style={styles.sectionLabel}>{t('settings.persona')}</Text>
      <Text style={styles.sectionDescription}>{t('settings.personaDescription')}</Text>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>{t('settings.personaStyleLabel')}</Text>
        <Text style={[styles.summaryValue, { color: theme.colors.text }]}>{styleName}</Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>{t('settings.personaRiskLabel')}</Text>
        <Text style={[styles.summaryValue, { color: theme.colors.text }]}>{riskLevel}</Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>{t('settings.personaLevelLabel')}</Text>
        <Text
          style={[styles.summaryValue, { color: theme.colors.text }]}
          testID="persona-current-level"
        >
          {persona ? t(LEVEL_LABEL_KEYS[persona.knowledge_level]) : t('settings.personaNotSet')}
        </Text>
      </View>

      <View
        style={[
          styles.pickerWrapper,
          {
            borderColor: theme.colors.border,
            borderWidth: theme.id === 'bauhaus' ? 2 : 1,
          },
        ]}
      >
        <Picker<'' | KnowledgeLevel>
          testID="persona-level-picker"
          selectedValue={persona?.knowledge_level ?? ''}
          onValueChange={handleLevelChange}
          enabled={!saving}
          style={[
            styles.picker,
            {
              color: theme.colors.text,
              backgroundColor: theme.colors.backgroundElevated,
            },
          ]}
          dropdownIconColor={theme.colors.textSecondary}
          mode="dropdown"
        >
          {!persona && (
            // No-selection placeholder: with no persona row yet the picker must
            // not silently PRESELECT 'beginner' — that state means unset, and
            // a preselect reads as a saved answer the user never gave.
            <Picker.Item
              label={t('settings.personaLevelPlaceholder', { defaultValue: 'Select a level' })}
              value=""
              color={theme.colors.textSecondary}
            />
          )}
          {KNOWLEDGE_LEVELS.map((level) => (
            <Picker.Item
              key={level}
              label={t(LEVEL_LABEL_KEYS[level])}
              value={level}
              color={theme.colors.text}
            />
          ))}
        </Picker>

        {Platform.OS === 'web' && (
          <style>{`
            select {
              background-color: ${theme.colors.backgroundElevated} !important;
              color: ${theme.colors.text} !important;
            }
            select option {
              background-color: ${theme.colors.backgroundElevated};
              color: ${theme.colors.text};
            }
          `}</style>
        )}
      </View>

      {saving && (
        <View style={styles.savingIndicator}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Text style={styles.savingText}>{t('common.saving')}</Text>
        </View>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    sectionContainer: {
      backgroundColor: theme.colors.surfaceSecondary,
      borderRadius: theme.radii.lg,
      padding: theme.spacing.lg,
      marginBottom: theme.spacing['2xl'],
      borderWidth: 2,
      borderColor: theme.colors.border,
    },
    sectionLabel: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.xs,
    },
    sectionDescription: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
      marginBottom: theme.spacing.md,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.sm,
    },
    summaryLabel: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
    },
    summaryValue: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: '500',
      flexShrink: 1,
      marginLeft: theme.spacing.md,
      textAlign: 'right',
    },
    pickerWrapper: {
      borderRadius: theme.radii.md,
      overflow: 'hidden',
      backgroundColor: theme.colors.backgroundElevated,
    },
    picker: {
      height: Platform.OS === 'web' ? 44 : undefined,
      fontSize: theme.typography.fontSizeMd,
    },
    savingIndicator: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: theme.spacing.sm,
    },
    savingText: {
      marginLeft: theme.spacing.sm,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
    },
  });
}
